import { useCallback, useEffect, useState } from 'react';

import type {
  GridApi,
  RowDragEvent,
  RowDropZoneParams,
} from '@mint-lib/ag-grid/community';

import type { FileSystemNode } from '../../../../types/fileSystem.js';
import { useFileSystemDnd } from './dndContext.js';

/**
 * Lets an element outside the grid accept rows dragged from it. The table drags
 * through AG Grid, not dnd-kit, so leaving the grid only fires rowDragLeave and
 * never reaches a droppable; an external drop zone is AG Grid's answer to that.
 */
export const useGridRowDropZone = (
  folderId: string,
  gridApi: GridApi<FileSystemNode> | null,
) => {
  const { checkMove, onMoveNodes } = useFileSystemDnd();

  // State, not a ref: the effect below has to re-run once the element mounts.
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [isOver, setIsOver] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);

  const getDraggedNodes = useCallback(
    (event: RowDragEvent<FileSystemNode>) =>
      event.nodes
        .map((node) => node.data)
        .filter((data): data is FileSystemNode => Boolean(data)),
    [],
  );

  useEffect(() => {
    if (!gridApi || !container) return;

    const params: RowDropZoneParams = {
      getContainer: () => container,
      onDragging: (event) => {
        setIsOver(true);
        setIsAllowed(checkMove(getDraggedNodes(event), folderId).allowed);
      },
      onDragLeave: () => setIsOver(false),
      onDragCancel: () => setIsOver(false),
      onDragStop: (event) => {
        setIsOver(false);

        const nodes = getDraggedNodes(event);

        if (!checkMove(nodes, folderId).allowed) return;

        onMoveNodes(nodes, folderId);
      },
    };

    gridApi.addRowDropZone(params);

    return () => {
      // The grid is torn down when the view switches, and the destroyed api
      // rejects further calls.
      if (gridApi.isDestroyed()) return;

      gridApi.removeRowDropZone(params);
    };
  }, [gridApi, container, folderId, checkMove, onMoveNodes, getDraggedNodes]);

  return {
    setGridDropRef: setContainer,
    isDropAllowed: isOver && isAllowed,
    isDropRejected: isOver && !isAllowed,
  };
};
